import type { FC } from 'react';
import { useState } from 'react';
import { Button as ChakraButton } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { ButtonProps } from '@chakra-ui/react';
import { useGTM, useNavigation } from '../../hooks';
import { GTMAddToBagEvent } from '../../hooks/gtm/model/events/GTMAddToBagEvent';
import { useSupports } from '../../hooks/useSupports';
import { getKioskLocationId } from '../../recaptcha/recaptcha-utils';
import { Button } from '../../ui-components';
import { sfccGet } from '../../utils/sitegenesis';
import { getQueryString } from '../../utils/url-utils';
import { useExtendWarranty } from '../extend-warranty/useExtendWarranty';
import { ApplePayIcon } from '../icons';
import styles from './ApplePay.module.scss';
import { postJson } from './utils';
import type {
    ApplePayCustomEvent,
    ApplePayProps,
    OnCancelHandlerFactory,
    OnPaymentAuthorizedHandlerFactory,
    OnPaymentAuthorizedHandlerRequest,
    OnPaymentAuthorizedHandlerResponse,
    OnPaymentMethodSelectedHandlerFactory,
    OnPaymentMethodSelectedHandlerRequest,
    OnPaymentMethodSelectedHandlerResponse,
    OnShippingContactSelectedHandlerFactory,
    OnShippingContactSelectedHandlerRequest,
    OnShippingContactSelectedHandlerResponse,
    OnShippingMethodSelectedHandlerFactory,
    OnShippingMethodSelectedHandlerRequest,
    OnShippingMethodSelectedHandlerResponse,
    OnValidateMerchantHandlerFactory,
    OnValidateMerchantHandlerRequest,
    OnValidateMerchantHandlerResponse,
    RequestParams,
    StepType,
    UrlActions,
} from './ApplePay.types';

export const APPLE_PAY_VERSION = 1;

const basketRequiredSteps: StepType[] = ['newpdp', 'newgdp'];

export const ApplePay: FC<ApplePayProps> = ({
    basket,
    product,
    applePayRequest,
    step,
    params = {},
    isDisabled = false,
    validationFn = (): boolean | Promise<boolean> => true,
}) => {
    // Default to true because we want it to behave the same way as before the fix for safari.
    const supportsNativeApplePayIcon = useSupports('-webkit-appearance: -apple-pay-button', true);
    const extend = useExtendWarranty();
    const navigate = useNavigation<unknown>();
    const intl = useIntl();
    const { onAddToBag } = useGTM();
    const [isBusy, setIsBusy] = useState(false);

    const getPrepareBasketParams = (): RequestParams => {
        const extendPlan = extend.getPlan();
        return {
            ...params,
            ...(extendPlan
                ? {
                      extendPlanId: extendPlan.planId,
                      extendPrice: extendPlan.price,
                      extendTerm: extendPlan.term,
                  }
                : {}),
        };
    };

    const createSession = (): void => {
        if (parseFloat(applePayRequest.total.amount) === 0) {
            applePayRequest.total.amount = '0.01';
        }
        const session = new ApplePaySession(APPLE_PAY_VERSION, applePayRequest);
        session.oncancel = onCancelHandler(session, step, params);
        session.onpaymentauthorized = onPaymentAuthorizedHandler(session, step, navigate, params);
        session.onpaymentmethodselected = onPaymentMethodSelectedHandler(
            session,
            applePayRequest,
            step,
            params,
        );
        session.onshippingcontactselected = onShippingContactSelectedHandler(
            session,
            applePayRequest,
            step,
            params,
        );
        session.onshippingmethodselected = onShippingMethodSelectedHandler(
            session,
            applePayRequest,
            step,
            params,
        );
        session.onvalidatemerchant = onValidateMerchantHandler(session, step, params);
        session.begin();
    };

    const prepareBasket = async (): Promise<void> => {
        if (basketRequiredSteps.includes(step)) {
            if (!params.pid) {
                return;
            }
            await sfccGet('Cart-SaveBasketItems', [{ format: 'ajax' }]).then(() =>
                postJson(urlsActions(step, undefined, getPrepareBasketParams()).prepareBasket, {
                    sku: params.pid,
                }).catch((err) => console.error(err)),
            );
        } else {
            await postJson(urlsActions(step, undefined, params).prepareBasket, {}).catch((err) =>
                console.error(err),
            );
        }
    };

    const onClick = async (): Promise<void> => {
        if (isBusy || isDisabled) return;
        const isFormValid = await validationFn();
        if (!isFormValid) return;
        if (product && basket) {
            onAddToBag(new GTMAddToBagEvent('APPLE_PAY', basket, product));

            const extendPlan = extend.getPlan();
            if (extendPlan) {
                const extendProduct = extend.buildExtendProductFromProduct(product);
                extendProduct.c_quantityAddedToBag = params.Quantity ?? 1;

                onAddToBag(new GTMAddToBagEvent('APPLE_PAY', basket, extendProduct));
            }
        }
        setIsBusy(true);
        try {
            createSession();
            await prepareBasket();
        } finally {
            setIsBusy(false);
        }
    };

    if (step === 'cart') {
        return (
            <div className="w-full">
                <Button
                    id="buy-box-apple-pay"
                    isDisabled={isBusy}
                    variant="secondary"
                    rightIcon={
                        <div className={styles.applePayButtonWhite}>
                            {supportsNativeApplePayIcon ? null : <ApplePayIcon />}
                        </div>
                    }
                    className="!w-full gap-2"
                    onClick={onClick}
                />
            </div>
        );
    }

    const buttonSecondaryStyle: StepType[] = ['newpdp', 'newgdp'];
    if (buttonSecondaryStyle.includes(step)) {
        return (
            <Button
                id="buy-box-apple-pay"
                data-testid="buy-box-apple-pay"
                isDisabled={isBusy}
                variant="secondary"
                rightIcon={
                    <div className={styles.applePayButtonWhite}>
                        {supportsNativeApplePayIcon ? null : <ApplePayIcon />}
                    </div>
                }
                className="!w-full gap-2"
                onClick={onClick}
            >
                {step === 'newpdp' ? (
                    <FormattedMessage id="buy.box.apple.pay" defaultMessage="Buy with" />
                ) : null}
            </Button>
        );
    }

    const buttonCaption = intl.formatMessage({
        defaultMessage: 'Checkout with',
        id: 'buttons.apple_pay.caption',
    });

    const buttonStyle = getButtonStyle(step, buttonCaption);

    return (
        <ChakraButton
            {...buttonStyle}
            isDisabled={isBusy}
            className="pwa-apple-pay-button"
            data-testid="apple-pay-button"
            onClick={onClick}
        />
    );
};

const getButtonStyle = (step: StepType, caption?: string): ButtonProps => {
    const base: ButtonProps = {
        width: '100%',
    };
    if (step === 'billing') {
        return {
            ...base,
            children: <ApplePayIcon />,
        };
    }

    return {
        ...base,
        colorScheme: 'plain',
        rightIcon: <ApplePayIcon />,
        children: caption,
    };
};

const getSFCCURL: (
    path: string,
    step: StepType,
    kioskLocationId?: string | null,
    params?: RequestParams,
) => string = (path, _step, kioskLocationId, params = {}) => {
    const queryString = getQueryString([
        {
            ...(kioskLocationId ? { 'kiosk-location-id': kioskLocationId } : {}),
            ...params,
        },
    ]);
    const url = `/on/demandware.store/Sites-SLT-Site/default${path}?${queryString}`;
    return url;
};

const urlsActions = (
    step: StepType,
    kioskLocationId?: string | null,
    params: RequestParams = {},
): UrlActions => ({
    cancel: getSFCCURL('/__SYSTEM__ApplePay-Cancel', step, kioskLocationId, params),
    onShippingContactSelected: getSFCCURL(
        '/__SYSTEM__ApplePay-ShippingContactSelected',
        step,
        kioskLocationId,
        params,
    ),
    onPaymentMethodSelected: getSFCCURL(
        '/__SYSTEM__ApplePay-PaymentMethodSelected',
        step,
        kioskLocationId,
        params,
    ),
    onValidateMerchant: getSFCCURL(
        '/__SYSTEM__ApplePay-ValidateMerchant',
        step,
        kioskLocationId,
        params,
    ),
    onPaymentAuthorized: getSFCCURL(
        '/__SYSTEM__ApplePay-PaymentAuthorized',
        step,
        kioskLocationId,
        params,
    ),
    prepareBasket: getSFCCURL('/__SYSTEM__ApplePay-PrepareBasket', step, kioskLocationId, params),
    onShippingMethodSelected: getSFCCURL(
        '/__SYSTEM__ApplePay-ShippingMethodSelected',
        step,
        kioskLocationId,
        params,
    ),
});

const onAbort = (session: ApplePaySession) => (): void => {
    try {
        session.abort();
    } catch (err) {
        // We are capturing this error, logging it only as a warning.
        // This will probably only happens if the session is already closed
        console.warn('Problem trying to abort session', err);
    }
};

/* eslint-disable */
function filterEvent<T>(e: ApplePayJS.Event): T {
    const filteredEvent = {};
    for (let prop in e) {
        if (!Event.prototype.hasOwnProperty(prop)) {
            // @ts-ignore
            filteredEvent[prop] = e[prop];
        }
    }
    return filteredEvent as unknown as T;
}

const onCancelHandler: OnCancelHandlerFactory =
    (_: ApplePaySession, step: StepType, params: RequestParams = {}) =>
    (event: ApplePayJS.Event): void => {
        postJson(urlsActions(step, undefined, params).cancel, filterEvent(event))
            .finally(async () => {
                if (basketRequiredSteps.includes(step)) {
                    await sfccGet('Cart-RestoreBasketItems', [{ format: 'ajax' }]);
                }
            })
            .catch((err) => console.error(err));
    };

const onPaymentAuthorizedHandler: OnPaymentAuthorizedHandlerFactory =
    (
        session: ApplePaySession,
        step: StepType,
        navigate: ReturnType<typeof useNavigation>,
        params: RequestParams = {},
    ) =>
    async (e: ApplePayJS.ApplePayPaymentAuthorizedEvent): Promise<void> => {
        const kioskLocationId = getKioskLocationId();
        return postJson<OnPaymentAuthorizedHandlerRequest, OnPaymentAuthorizedHandlerResponse>(
            urlsActions(step, kioskLocationId, params).onPaymentAuthorized,
            filterEvent<OnPaymentAuthorizedHandlerRequest>(e),
        )
            .then((response) => {
                if (response.redirect) {
                    session.completePayment(ApplePaySession.STATUS_SUCCESS);
                    const redirectURL = new URL(response.redirect).pathname;
                    navigate(redirectURL);
                } else if (response.code?.includes(':')) {
                    const idx: number = response.code.indexOf(':');
                    const code: ApplePayJS.ApplePayErrorContactField = response.code.substring(
                        0,
                        idx,
                    ) as ApplePayJS.ApplePayErrorContactField;
                    const message = response.code.substring(idx + 1);
                    const errorField: ApplePayJS.ApplePayErrorCode =
                        code === 'addressLines' ? 'addressUnserviceable' : 'billingContactInvalid';
                    session.completePayment({
                        status: ApplePaySession.STATUS_FAILURE,
                        errors: [new ApplePayError(errorField, code, message)],
                    });
                } else {
                    session.completePayment(ApplePaySession.STATUS_FAILURE);
                }
            })
            .catch(() => {
                session.completePayment({
                    status: ApplePaySession.STATUS_FAILURE,
                    errors: [new ApplePayError('unknown', undefined, 'Payment failed')],
                });
            })
            .finally(async () => {
                if (basketRequiredSteps.includes(step)) {
                    await sfccGet('Cart-RestoreBasketItems', [{ format: 'ajax' }]);
                }
            });
    };

function dispatchEvent(event: ApplePayCustomEvent): void {
    if (!event || !event.name) {
        return;
    }
    document.body.dispatchEvent(
        new CustomEvent(event.name, {
            bubbles: true,
            detail: event.detail,
        }),
    );
}

function processServerResponse(response: { redirect?: string; event?: ApplePayCustomEvent }): void {
    if (!response) {
        return;
    }
    if (response.event) {
        dispatchEvent(response.event);
    }
}

const onPaymentMethodSelectedHandler: OnPaymentMethodSelectedHandlerFactory =
    (
        session: ApplePaySession,
        applePayRequest: ApplePayJS.ApplePayPaymentRequest,
        step: StepType,
        params: RequestParams = {},
    ) =>
    async (e: ApplePayJS.ApplePayPaymentMethodSelectedEvent): Promise<void> =>
        postJson<OnPaymentMethodSelectedHandlerRequest, OnPaymentMethodSelectedHandlerResponse>(
            urlsActions(step, undefined, params).onPaymentMethodSelected,
            filterEvent<OnPaymentMethodSelectedHandlerRequest>(e),
        )
            .then((response) => {
                if (response.total && response.lineItems) {
                    session.completePaymentMethodSelection(response.total, response.lineItems);
                    processServerResponse(response);
                } else if (response.code?.includes(':')) {
                    const idx: number = response.code.indexOf(':');
                    const code: ApplePayJS.ApplePayErrorContactField = response.code.substring(
                        0,
                        idx,
                    ) as ApplePayJS.ApplePayErrorContactField;
                    const message: string = response.code.substring(idx + 1);
                    session.completePaymentMethodSelection({
                        newTotal: applePayRequest.total,
                        newLineItems: applePayRequest.lineItems ?? [],
                        errors: [new ApplePayError('billingContactInvalid', code, message)],
                    });
                } else {
                    session.completePaymentMethodSelection(
                        applePayRequest.total,
                        applePayRequest.lineItems ?? [],
                    );
                }
            })
            .catch((err: Error) => console.error(err));

const onShippingContactSelectedHandler: OnShippingContactSelectedHandlerFactory =
    (
        session: ApplePaySession,
        applePayRequest: ApplePayJS.ApplePayPaymentRequest,
        step: StepType,
        params: RequestParams = {},
    ) =>
    async (e: ApplePayJS.ApplePayShippingContactSelectedEvent): Promise<void> =>
        postJson<OnShippingContactSelectedHandlerRequest, OnShippingContactSelectedHandlerResponse>(
            urlsActions(step, undefined, params).onShippingContactSelected,
            filterEvent<OnShippingContactSelectedHandlerRequest>(e),
        )
            .then((response) => {
                if (response.shippingMethods && response.total && response.lineItems) {
                    session.completeShippingContactSelection(
                        ApplePaySession.STATUS_SUCCESS,
                        response.shippingMethods,
                        response.total,
                        response.lineItems,
                    );
                    processServerResponse(response);
                } else if (response.code?.includes(':')) {
                    const idx: number = response.code.indexOf(':');
                    const code: ApplePayJS.ApplePayErrorContactField = response.code.substring(
                        0,
                        idx,
                    ) as ApplePayJS.ApplePayErrorContactField;
                    const message = response.code.substring(idx + 1);
                    session.completeShippingContactSelection({
                        newTotal: applePayRequest.total,
                        newLineItems: applePayRequest.lineItems ?? [],
                        errors: [new ApplePayError('shippingContactInvalid', code, message)],
                    });
                    processServerResponse(response);
                } else {
                    session.completeShippingContactSelection(
                        ApplePaySession.STATUS_INVALID_SHIPPING_CONTACT,
                        [],
                        applePayRequest.total,
                        applePayRequest.lineItems ?? [],
                    );
                    processServerResponse(response);
                }
            })
            .catch((err) => console.error(err));

const onShippingMethodSelectedHandler: OnShippingMethodSelectedHandlerFactory =
    (
        session: ApplePaySession,
        applePayRequest: ApplePayJS.ApplePayPaymentRequest,
        step: StepType,
        params: RequestParams = {},
    ) =>
    async (e: ApplePayJS.ApplePayShippingMethodSelectedEvent): Promise<void> =>
        postJson<OnShippingMethodSelectedHandlerRequest, OnShippingMethodSelectedHandlerResponse>(
            urlsActions(step, undefined, params).onShippingMethodSelected,
            filterEvent<OnShippingMethodSelectedHandlerRequest>(e),
        )
            .then((response) => {
                if (response.total && response.lineItems) {
                    session.completeShippingMethodSelection(
                        ApplePaySession.STATUS_SUCCESS,
                        response.total,
                        response.lineItems,
                    );
                } else {
                    session.completeShippingMethodSelection(
                        ApplePaySession.STATUS_FAILURE,
                        applePayRequest.total,
                        applePayRequest.lineItems ?? [],
                    );
                }
            })
            .catch((err) => console.error(err));

const onValidateMerchantHandler: OnValidateMerchantHandlerFactory =
    (session: ApplePaySession, step: StepType, params: RequestParams = {}) =>
    async (e: ApplePayJS.ApplePayValidateMerchantEvent): Promise<void> =>
        postJson<OnValidateMerchantHandlerRequest, OnValidateMerchantHandlerResponse>(
            urlsActions(step, undefined, params).onValidateMerchant,
            {
                ...filterEvent<OnValidateMerchantHandlerRequest>(e),
                isTrusted: true,
                hostname: window.location.hostname,
                validationURL: e.validationURL,
            },
        )
            .then((response) => session.completeMerchantValidation(response.session))
            .catch((err) => {
                console.error(err);
                onAbort(session);
            });
